import Annotation from '@/fhirworks/types/Annotation';
import CodeableConcept from '@/fhirworks/types/CodeableConcept';
import Identifier from '@/fhirworks/types/Identifier';
import Reference from '@/fhirworks/types/Reference';
import merge from 'lodash/merge';
import CommunicationPayload from './CommunicationPayload';
import DomainResource from './DomainResource';

/**
 * FHIR R4 Communication resource — one message inside a BN_MessageThread.
 *
 * `status` is 'completed' once the message is sent. The delivery channel uses the core
 * `medium` field (portal | sms | in-app) — there is no custom channel extension. Ordering
 * within a thread uses the core `sent` timestamp — there is no custom ordering extension.
 * `recipient` is the frozen delivery set captured at send time and IS the visibility control
 * for the message.
 *
 * First-class extensions:
 * - `thread` Reference: the owning BN_MessageThread. Every message belongs to exactly one.
 * - `privateToStaff` Boolean: staff-only visibility. Defaults to false and is always written,
 *   because the portal policy gate searches privateToStaff=false and a FHIR search for =false
 *   does not match a resource where the extension is absent.
 * - `editedAt` String (dateTime): when the sender last edited the message. Absent means never
 *   edited, and its presence is what the reader is shown as an "edited" marker. Editing rewrites
 *   `payload` and stamps this; it never touches `recipient`, `sender` or `sent`.
 */
export default class Communication extends DomainResource {
    static __className = 'Communication';
    __objectStructure = {
        identifier: [Identifier],
        status: String,
        category: [CodeableConcept],
        medium: [CodeableConcept],
        subject: Reference,
        sender: Reference,
        recipient: [Reference],
        sent: String,
        received: String,
        payload: [CommunicationPayload],
        note: [Annotation],

        // first class extension
        thread: Reference,
        privateToStaff: Boolean,
        editedAt: String,
    };

    __objectDefaults = {
        privateToStaff: false,
    };

    constructor(constructJson, className = 'Communication') {
        super(constructJson, className);
        this.createAndPopulateStructure(this.__objectStructure, constructJson, this.__objectDefaults);
        this.originalObjJson = this.toJSON();
    }

    toJSON() {
        return merge(super.toJSON(this), this.getJsonForStructure(this.__objectStructure));
    }
}
